import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { ProgressBar } from "@/components/design/ProgressBar";
import {
  StepChain,
  StepChainContent,
  StepChainItem,
  StepChainStep,
  StepChainTrigger,
  type StepChainOrientation,
  type StepChainStatus,
} from "@/components/design/StepChain";
import { ProgressStageStatus } from "@/graphql/generated/types";
import { detailLabel, PROGRESS_MESSAGES, stageCounter, stageLabel } from "@/lib/progress-labels";
import type { OperationProgress } from "@/lib/use-operation-progress";

// Maps the progress contract onto the design primitives: planned stages become a StepChain, a
// progress without stages becomes one ProgressBar. Nothing here knows which operation it shows.
// Until the first snapshot arrives there is no shape to draw yet, so it breathes neutrally instead
// of guessing one; a counter is only shown where the server sent a total.

type Detail = OperationProgress["details"][number];

const STATUS: Record<ProgressStageStatus, StepChainStatus> = {
  [ProgressStageStatus.Pending]: "pending",
  [ProgressStageStatus.Active]: "active",
  [ProgressStageStatus.Done]: "done",
  [ProgressStageStatus.Skipped]: "skipped",
  [ProgressStageStatus.Failed]: "failed",
};

function toStatus(status: string): StepChainStatus {
  return (STATUS as Record<string, StepChainStatus | undefined>)[status] ?? "pending";
}

// How long the running stage has been running, measured in this browser from the moment it was first
// shown as running: a fact, not a forecast. Mounted only while its stage runs, so every stage starts at
// zero without any bookkeeping. Browser time on purpose: a server start time would be off by however far
// the two clocks disagree, the up-to-a-second delay of the stream is the smaller error.
function ElapsedSeconds() {
  const intl = useIntl();
  const [startedAt] = useState(() => Date.now());
  const [now, setNow] = useState(startedAt);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const seconds = Math.max(0, Math.floor((now - startedAt) / 1000));
  return seconds < 60
    ? intl.formatMessage(PROGRESS_MESSAGES.elapsedSeconds, { seconds })
    : intl.formatMessage(PROGRESS_MESSAGES.elapsedMinutes, { minutes: Math.floor(seconds / 60), seconds: seconds % 60 });
}

export function OperationProgressView({
  progress,
  orientation = "horizontal",
}: {
  progress: OperationProgress | null;
  orientation?: StepChainOrientation;
}) {
  const intl = useIntl();
  const label = intl.formatMessage(PROGRESS_MESSAGES.label);

  // Keyed by the fact, not by its text: a line whose count moves must update in place instead of
  // remounting and replaying its entrance animation on every tick.
  const detailLines = (details: readonly Detail[]) =>
    details.flatMap((detail) => {
      const message = detailLabel(detail.key);
      return message
        ? [{ key: detail.key, text: intl.formatMessage(message, { count: detail.count ?? 0, total: detail.total ?? 0 }) }]
        : [];
    });

  if (!progress) {
    return (
      <div className="flex flex-col gap-2" role="status">
        <ProgressBar label={label} />
        <p className="text-smycles-label text-ink-muted">{intl.formatMessage(PROGRESS_MESSAGES.waiting)}</p>
      </div>
    );
  }

  if (progress.stages.length === 0) {
    const lines = detailLines(progress.details);
    return (
      <div className="flex flex-col gap-2">
        <ProgressBar label={label} done={progress.done} total={progress.total} valueAlreadyVisible />
        {progress.total != null ? (
          <p className="text-smycles-label text-ink-muted">
            {intl.formatMessage(PROGRESS_MESSAGES.counter, { done: progress.done ?? 0, total: progress.total })}
          </p>
        ) : null}
        {lines.map((line) => (
          <p key={line.key} className="text-smycles-meta text-ink-muted">
            {line.text}
          </p>
        ))}
      </div>
    );
  }

  const running = progress.stages.find((stage) => toStatus(stage.status) === "active");
  return (
    <>
      {/* Announce stage changes only; the counters tick too often to be read out. */}
      <span className="sr-only" aria-live="polite">
        {running ? intl.formatMessage(stageLabel(running.key)) : ""}
      </span>
      <StepChain label={label} orientation={orientation}>
        {progress.stages.map((stage) => {
          const items = [
            ...(stage.total != null
              ? [{ key: "counter", text: intl.formatMessage(stageCounter(stage.key), { done: stage.done ?? 0, total: stage.total }) }]
              : []),
            ...detailLines(stage.details),
          ];
          return (
            <StepChainStep key={stage.key} status={toStatus(stage.status)} collapsible={items.length > 0}>
              <StepChainTrigger meta={toStatus(stage.status) === "active" ? <ElapsedSeconds /> : null}>
                {intl.formatMessage(stageLabel(stage.key))}
              </StepChainTrigger>
              {items.length > 0 ? (
                <StepChainContent>
                  {items.map((item) => (
                    <StepChainItem key={item.key}>{item.text}</StepChainItem>
                  ))}
                </StepChainContent>
              ) : null}
            </StepChainStep>
          );
        })}
      </StepChain>
    </>
  );
}
